import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import {useTranslation} from "react-i18next";
import {
    Alert,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    OutlinedInput
} from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import React, {KeyboardEvent, useEffect, useState} from "react";
import {logInUser} from "../../../../store/auth/ActionCreators";
import {VisibilityOffOutlined, VisibilityOutlined} from "@mui/icons-material";
import {RootState, useAppDispatch, useAppSelector} from "../../../../store";
import {validateEmail} from "../../../../util/regex";
import {getEmailHelperText, getPasswordHelperText, getSafeBackendErrorMessage} from "../../../../util/utils";
import {RoutePaths} from "../../../../util/constants";
import {loginReset} from "../../../../store/auth/Slice";
import ReactGA from "react-ga4";
import {Link as RouterLink} from "react-router-dom";
import ResendEmail from "../../../shared/resend-email/ResendEmail";
import AuthService from "../../../../api/authService";
import {toastError} from "../../../shared/toast/Toast";

const LoginForm = () => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const loginLoading = useAppSelector((state: RootState) => state.authReducer.login.loading)
    const loginError = useAppSelector((state: RootState) => state.authReducer.login.error)
    const loginErrorMessage = useAppSelector((state: RootState) => state.authReducer.login.errorMessage)
    const [email, setEmail] = useState<string>("")
    const [lastEmail, setLastEmail] = useState<string>("")
    const [password, setPassword] = useState<string>("")
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [counter, setCounter] = useState(0)
    let interval: NodeJS.Timeout

    useEffect(() => {
        dispatch(loginReset())
    }, [dispatch]);

    const handleOnKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleOnLoginClick()
        }
    }

    const handleOnLoginClick = () => {
        ReactGA.event("login", {method: "Standard"})
        setLastEmail(email)
        dispatch(logInUser({email, password}))
    }

    const handleClickShowPassword = () => {
        setShowPassword((oldShowPassword) => !oldShowPassword)
    }

    const handleResendEmailClick = () => {
        setCounter(30)
        AuthService.resendConfirmation({email: lastEmail})
            .then()
            .catch((e) => {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
            })
            .finally(() => {
                interval = setInterval(() => {
                    setCounter((oldCounter) => {
                        if (oldCounter === 1) clearInterval(interval)
                        return oldCounter - 1
                    })
                }, 1000)
            })
    }

    const isConfirmEmailError = loginErrorMessage.includes('не е верифициран') ||
        loginErrorMessage.includes('nuk është verifikuar') ||
        loginErrorMessage.includes('is not verified')

    return (
        <Box sx={{mt: 1}}>
            {loginError && <Alert severity="error">
                {loginErrorMessage}
                {isConfirmEmailError &&
                    <ResendEmail counter={counter} handleOnSubmitClick={handleResendEmailClick} color='secondary'/>}
            </Alert>}
            <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                type="email"
                helperText={getEmailHelperText(email, loginError)}
                error={loginError && !validateEmail(email)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                label={t('e-mail')}
                name="email"
                autoComplete="email"
                color="primary"
                onKeyDown={handleOnKeyPress}
            />
            <FormControl margin="normal" fullWidth variant="outlined">
                <InputLabel error={loginError && !password.length}
                            required
                            color="primary"
                            htmlFor="password">{t('password')}</InputLabel>
                <OutlinedInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    required
                    color="primary"
                    error={loginError && !password.length}
                    autoComplete="current-password"
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleOnKeyPress}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={(e) => e.preventDefault()}
                                color={loginError && !password.length ? 'error' : 'primary'}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOffOutlined/> : <VisibilityOutlined/>}
                            </IconButton>
                        </InputAdornment>
                    }
                    label={t('password')}
                />
                {loginError && !password.length && (
                    <FormHelperText error>
                        {getPasswordHelperText(password, loginError)}
                    </FormHelperText>
                )}
            </FormControl>
            <Link color="primary"
                  sx={{cursor: "pointer"}}
                  component={RouterLink}
                  to={RoutePaths.FORGOT_PASSWORD}>
                {t('forgot_password')}?
            </Link>
            <LoadingButton
                fullWidth
                loading={loginLoading}
                variant="contained"
                color="primary"
                onClick={handleOnLoginClick}
                sx={{mt: 3, mb: 1}}
            >
                {t('login')}
            </LoadingButton>
        </Box>
    )
}

export default LoginForm
